import { FC, useEffect } from "react";
import { Router } from "next/router";

import { atom, PrimitiveAtom, useAtom } from "jotai";
import { useHydrateAtoms } from "jotai/utils";
import { pageviewFB } from "~lib";
import { ENABLE_TRACKING_SCRIPTS } from "~features/tracking/config";

declare interface DataLayerWindow extends Window {
  fbq: any;
  GA_INITIALIZED?: boolean;
}
declare const window: DataLayerWindow;

export const pixelIdAtom = atom<string | undefined>(undefined) as PrimitiveAtom<
  string | undefined
>;

export const useFacebookPixelScript = (pixelId: string) => {
  const [, setPixelId] = useAtom(pixelIdAtom);

  useEffect(() => {
    if (pixelId) {
      setPixelId(pixelId);
    }
  }, [pixelId, setPixelId]);

  useEffect(() => {
    if (pixelId && ENABLE_TRACKING_SCRIPTS) {
      (function (f, b, e, v, n, t, s) {
        if (f.fbq) return;
        n = f.fbq = function (...args: any[]) {
          n.callMethod ? n.callMethod(...args) : n.queue.push(args);
        };
        if (!(f as any)._fbq) (f as any)._fbq = n;
        n.push = n;
        n.loaded = true;
        n.version = "2.0";
        n.queue = [];
        t = b.createElement(e) as any;
        t.async = true;
        t.defer = true;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
      })(
        window,
        document,
        "script",
        "https://connect.facebook.net/en_US/fbevents.js"
      );
      // disable autoConfig events like MicroData or SubscribedButtonClick
      // since they will only be sent to the first loaded pixel(organisation pixel).
      window.fbq("set", "autoConfig", false, pixelId);
      window.fbq("init", pixelId);
      // disable implicit PageView since they will be sent to all the pixels.
      window.fbq.disablePushState = true;

      pageviewFB(pixelId);
    }
  }, [pixelId]);
};

interface FacebookPixelScriptProps {
  pixelId?: string;
  router?: Router;
}

export const FacebookPixelScript: FC<FacebookPixelScriptProps> = ({
  pixelId,
}) => {
  useFacebookPixelScript(pixelId);
  return null;
};

export const FacebookPixelRouteTracker: FC<FacebookPixelScriptProps> = ({
  pixelId: _pixelId,
  router,
}) => {
  useHydrateAtoms([[pixelIdAtom, _pixelId]]);
  const [pixelId] = useAtom(pixelIdAtom);

  useEffect(() => {
    if (!router || !pixelId) return;

    const handleRouteChange = () => {
      if (ENABLE_TRACKING_SCRIPTS) {
        pageviewFB(pixelId);
      }
    };

    router.events.on("routeChangeComplete", handleRouteChange);
    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, [router?.events, pixelId, router]);

  return null;
};
